import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, GitBranch, Loader2, Plus, AlertCircle } from 'lucide-react';
import { supabase } from '../../utils/supabaseStorage';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { creatorCreateNewDraft } from '../../utils/creatorApi';
import {
  VERSION_STATUS_LABEL,
  VERSION_STATUS_TONE,
  type AgentTemplateVersionRow,
  type AgentTemplateVersionStatus,
  type CreatorAgentTemplateRow,
} from '../../types/creator';

export function StudioAgentVersionsPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CreatorAgentTemplateRow | null>(null);
  const [versions, setVersions] = useState<AgentTemplateVersionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);

  const load = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    setError(null);

    const { data: tplRow, error: tplErr } = await supabase
      .from('agent_templates')
      .select(
        'id, name, slug, hy_user_id, template_kind, version_status, current_version_id, paused_reason, rewards_enabled, is_active, cover_media_url, cover_media_type, public_description, updated_at, created_at'
      )
      .eq('id', templateId)
      .maybeSingle();

    if (tplErr || !tplRow) {
      setError(tplErr?.message || 'Agent not found');
      setLoading(false);
      return;
    }
    setTemplate(tplRow as CreatorAgentTemplateRow);

    const { data: versionRows, error: vErr } = await supabase
      .from('agent_template_versions')
      .select('*')
      .eq('template_id', templateId)
      .order('version_number', { ascending: false });
    if (vErr) {
      setError(vErr.message);
      setLoading(false);
      return;
    }
    setVersions((versionRows ?? []) as AgentTemplateVersionRow[]);
    setLoading(false);
  }, [templateId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleNewDraft = async () => {
    if (!templateId) return;
    setCreatingDraft(true);
    setError(null);
    const result = await creatorCreateNewDraft({ templateId });
    setCreatingDraft(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    navigate(`/studio/agents/${templateId}`);
  };

  const hasDraft = versions.some((v) => v.version_status === 'draft');

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="max-w-5xl mx-auto w-full px-4 pt-4 pb-3 flex flex-wrap items-center justify-between gap-3 shrink-0">
        <button
          type="button"
          onClick={() => navigate('/studio')}
          className="text-th-secondary hover:text-th-primary inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Your agents
        </button>
        <div className="flex flex-wrap gap-2">
          {hasDraft ? (
            <button
              type="button"
              onClick={() => navigate(`/studio/agents/${templateId}`)}
              className="button-primary text-sm inline-flex items-center gap-1.5"
            >
              Open active draft
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleNewDraft()}
              disabled={creatingDraft}
              className="button-primary text-sm inline-flex items-center gap-1.5"
            >
              {creatingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              New draft
            </button>
          )}
        </div>
      </div>

      <main className="min-h-0 flex-1 overflow-y-auto max-w-5xl mx-auto w-full px-4 pb-12 space-y-3">
        <header className="card p-4 flex items-start gap-3 flex-wrap">
          <GitBranch className="w-5 h-5 text-brand mt-0.5" />
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-th-primary tracking-tight truncate">
              {template?.name || 'Version history'}
            </h1>
            <p className="text-xs text-th-secondary mt-1">
              All revisions (newest first). Live versions cannot be edited \u2014 start a new draft to revise.
            </p>
          </div>
        </header>

        {error && (
          <div className="p-3 bg-status-error/10 border border-status-error/30 rounded-lg flex items-start gap-2 text-status-error text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : versions.length === 0 ? (
          <p className="text-center text-th-secondary py-10">No versions yet.</p>
        ) : (
          <ul className="space-y-2">
            {versions.map((v) => (
              <VersionRow
                key={v.id}
                version={v}
                onOpenDraft={() => navigate(`/studio/agents/${templateId}`)}
              />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function VersionRow({
  version,
  onOpenDraft,
}: {
  version: AgentTemplateVersionRow;
  onOpenDraft: () => void;
}) {
  const status: AgentTemplateVersionStatus = version.version_status;
  const tone = VERSION_STATUS_TONE[status];
  const label = VERSION_STATUS_LABEL[status];
  const payload = (version.payload || {}) as Record<string, unknown>;
  const changelog = typeof payload.changelog === 'string' ? payload.changelog : '';
  const reason = version.decision_reason;

  return (
    <li className="card p-4 space-y-2">
      <div className="flex items-center flex-wrap gap-3 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-th-primary">v{version.version_number}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${tone}`}>{label}</span>
          <span className="text-xs text-th-tertiary">
            {new Date(version.updated_at).toLocaleString()}
          </span>
        </div>
        {status === 'draft' && (
          <button type="button" className="button-secondary text-xs" onClick={onOpenDraft}>
            Open draft
          </button>
        )}
      </div>
      {changelog && (
        <p className="text-xs text-th-secondary whitespace-pre-wrap">
          <span className="font-semibold text-th-primary">Changelog:</span> {changelog}
        </p>
      )}
      {reason && (
        <p
          className={`text-xs whitespace-pre-wrap ${
            status === 'rejected' ? 'text-status-error' : 'text-th-secondary'
          }`}
        >
          <span className="font-semibold">Reviewer note:</span> {reason}
        </p>
      )}
    </li>
  );
}
